
import apiClient from './apiClient';

// Update user profile
export const updateUserProfile = async (userId, updateData) => {
  try {
    const response = await apiClient.put(`/api/users/${userId}`, updateData);
    return response.data;
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to update profile'
    };
  }
};

// Change user password
export const changeUserPassword = async (userId, newPassword) => {
  try {
    const response = await apiClient.put(`/api/users/${userId}/password`, { password: newPassword });
    return response.data;
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to change password'
    };
  }
};

const API_SIGNUP_URL = "/api/users"; // Create user endpoint
const API_LOGIN_URL = "/api/authentication/login"; // Authentication endpoint

export const signup = async (userData) => {
  // Map frontend account types to backend expected values
  const accountTypeMap = {
    'solo': 'Solo', 
    'solo_assistant': 'Solo_Assistant',
    'small_team': 'Small_Team',
    'medium_team': 'Medium_Team', 
    'large_team': 'Large_Team',
    'small_brokerage': 'Small_Brokerage',
    'medium_brokerage': 'Medium_Brokerage',
    'large_brokerage': 'Large_Brokerage',
    'trial': 'Trial'  // Add trial account type mapping
  };
  
  // Normalize the account type for backend
  const normalizedAccountType = userData.accountType?.toLowerCase().replace(/ /g, '_');
  const backendAccountType = accountTypeMap[normalizedAccountType] || userData.accountType;
  
  // Make sure email is lowercase for consistency
  const userDataNormalized = {
    name: userData.name,
    last_name: userData.last_name || '',
    email: userData.email.toLowerCase(),
    password: userData.password,
    company: userData.company || '',
    accountType: backendAccountType,
    phoneSlots: userData.phoneSlots || 0,
    billingCycle: userData.billingCycle || 'monthly',
    paymentMethodId: userData.paymentMethodId,
    isTrialMode: userData.isTrialMode || false,
    trialDurationDays: userData.trialDurationDays || undefined
  };
  
  console.log('🚀 Signup attempt with data:', userDataNormalized);
  console.log('🔄 Account type mapping:', normalizedAccountType, '→', backendAccountType);
  console.log('🛠️ Using backend API at https://api-dev.smartclose.app');
  
  // Real API call to backend
  try {
    console.log('📡 Making API call to:', API_SIGNUP_URL);
    console.log('📤 Sending data:', userDataNormalized);
    console.log('🌐 API Base URL:', apiClient.defaults.baseURL);
    console.log('🔗 Full URL:', `${apiClient.defaults.baseURL}${API_SIGNUP_URL}`);
    
    const response = await apiClient.post(API_SIGNUP_URL, userDataNormalized);
    console.log('✅ Signup API success:', response.data);
    console.log('✅ Response status:', response.status);
    console.log('✅ Response headers:', response.headers);
    return response.data;
  } catch (error) {
    console.error('❌ Signup API error:', error);
    console.error('❌ Error response:', error.response?.data);
    console.error('❌ Error status:', error.response?.status);
    console.error('❌ Error headers:', error.response?.headers);
    console.error('❌ Network error?', !error.response);
    
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'An error occurred during signup'
    };
  }
};

export const login = async (credentials) => {
  console.log('🔐 Login attempt with credentials:', { email: credentials.email, password: '***' });
  console.log('🛠️ Using backend API at https://api-dev.smartclose.app');
  
  // Make sure email is lowercase for consistency
  const credentialsNormalized = {
    ...credentials,
    email: credentials.email.toLowerCase()
  };
  
  // Real API call to backend
  try {
    const response = await apiClient.post(API_LOGIN_URL, credentialsNormalized);
    
    if (response.data.success) {
      console.log('🎉 Successful backend login:', { 
        email: response.data.user?.email, 
        accountType: response.data.user?.accountType 
      });
    }
    
    return response.data;
  } catch (error) {
    console.error('❌ Backend login failed:', error.response?.data || error.message);
    return {
      success: false,
      message: error.response?.data?.message || 'An error occurred during login'
    };
  }
};

export const forgotPassword = async (email) => {
  // Make sure email is lowercase for consistency
  const normalizedEmail = email.toLowerCase();
  
  // Real API call to backend
  try {
    const response = await apiClient.post(`${API_LOGIN_URL}/forgot-password`, { email: normalizedEmail });
    return response.data;
  } catch (error) {
    console.error('❌ Backend forgot password failed:', error.response?.data || error.message);
    return {
      success: false,
      message: error.response?.data?.message || 'An error occurred while processing your request'
    };
  }
};

// Get current user from local storage
export const getCurrentUser = () => {
  const userJson = localStorage.getItem('user');
  return userJson ? JSON.parse(userJson) : null;
};

// Set user to local storage
export const setCurrentUser = (user, token) => {
  localStorage.setItem('user', JSON.stringify(user));
  localStorage.setItem('token', token);
};

// Remove user from local storage
export const removeCurrentUser = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('token');
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!localStorage.getItem('token');
};

// Get user profile from server — re-derives live fields like can_manage_teams from Connections
export const getUserProfile = async () => {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return { success: false, message: 'User not authenticated' };
  }

  try {
    const response = await apiClient.get('/api/authentication/profile');
    if (response.data?.success && response.data?.user) {
      return { success: true, user: response.data.user };
    }
  } catch {
    // Fall back to cached user if the endpoint is unavailable
  }

  return { success: true, user: currentUser };
};

// Reset password with token
export const resetPassword = async ({ email, token, password }) => {
  try {
    const response = await apiClient.post('/api/authentication/login/reset-password', {
      email,
      token,
      password
    });
    return response.data;
  } catch (error) {
    console.error('Reset password error:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to reset password'
    };
  }
};

